import Vue from 'vue'
import {
  ValidationProvider,
  ValidationObserver,
  setInteractionMode,
  extend
} from 'vee-validate'
import {
  required,
  email,
  regex,
  alpha_spaces,
  alpha_dash,
  min,
  max,
  integer,
  min_value,
  max_value,
  confirmed,
  between
} from 'vee-validate/dist/rules'
import moment from 'moment'
import {
  PROFILE_PHOTO_MESSAGES,
  isAllowedProfilePhotoType,
  isAllowedProfilePhotoSize
} from '@/modules/common/utils/profile-photo-validation.util'

extend('is_not_negative_number', {
  validate (value) {
    return !isNaN(value) && value >= 0
  },
  message: 'This field must not be a negative number'
})

extend('is_positive_number', {
  validate (value) {
    return !isNaN(value) && value >= 1
  },
  message: 'This field must be a positive number'
})

extend('is_number', {
  validate (value) {
    const num = Number(value)
    return !isNaN(num)
  },
  message: 'This field must be a number'
})

extend('contains_uppercase', {
  validate (value) {
    return /[A-Z]/.test(value)
  },
  message: 'Password must contain an uppercase character'
})

extend('contains_lowercase', {
  validate (value) {
    return /[a-z]/.test(value)
  },
  message: 'Password must contain a lowercase character'
})

extend('contains_number', {
  validate (value) {
    return /[0-9]/.test(value)
  },
  message: 'Password must contain a number'
})

extend('contains_special', {
  validate (value) {
    return /[^A-Za-z0-9]/.test(value)
  },
  message: 'Password must contain a special character'
})

extend('date_greater_than_or_equals_now', {
  validate (value) {
    return moment(value).isSameOrAfter(moment().utc().format('YYYY-MM-DD'))
  },
  message: 'This date must be equals or after today'
})

extend('date_greater_before_or_equals_now', {
  validate (value) {
    return moment(value).isSameOrBefore(moment().utc().format('YYYY-MM-DD'))
  },
  message: 'This date must be equals or before today'
})

extend('datetime_greater_than_or_equals_now', {
  validate (value) {
    return moment(value).isSameOrAfter(moment().utc())
  },
  message: 'This date time must be equals or after today'
})

extend('isBeforeDate', {
  params: ['date'],
  validate (value, { date }: Record<string, any>) {
    if (date) {
      return moment(value).isBefore(moment(date).utc())
    } else {
      return true
    }
  },
  message: 'This date must be less than {_field_}'
})

extend('isSameOrBeforeDate', {
  params: ['date'],
  validate (value, { date }: Record<string, any>) {
    if (date) {
      return moment(value).isSameOrBefore(moment(date).utc())
    } else {
      return true
    }
  },
  message: (fieldName, placeholders) => {
    return `${fieldName} must be less than or equal to ${moment(placeholders._date_).format('MM-DD-YYYY')}`
  }
})

extend('isAfterDate', {
  params: ['date'],
  validate (value, { date }: Record<string, any>) {
    if (date) {
      return moment(value).isAfter(moment(date).utc())
    } else {
      return true
    }
  },
  message: 'This date must be greater than {_field_}'
})

extend('isSameOrAfterDate', {
  params: ['date'],
  validate (value, { date }: Record<string, any>) {
    if (date) {
      return moment(value).isSameOrAfter(moment(date).utc())
    } else {
      return true
    }
  },
  message: (fieldName, placeholders) => {
    return `${fieldName} must be greater than or equal to ${moment(placeholders._date_).format('MM-DD-YYYY')}`
  }
})

extend('required', {
  ...required,
  message: 'This field is required'
})

extend('integer', {
  ...integer,
  message: 'Please enter an integer'
})

extend('email', {
  ...email,
  message: 'Please enter a valid email'
})

extend('regex', regex)

extend('min', {
  ...min,
  message: 'Please enter at least {length} characters'
})

extend('alpha_spaces', {
  ...alpha_spaces,
  message: 'Please enter only letters or spaces'
})

extend('alpha_num_space', {
  validate (value) {
    return /^[A-Za-z0-9 ]+$/.test(value)
  },
  message:
    'Please enter only alphabetic characters, numbers, or spaces'
})

extend('alpha_dash', {
  ...alpha_dash,
  message:
    'Please enter only alphabetic characters, numbers, dashes or underscores'
})

extend('confirmed', {
  ...confirmed,
  message: 'These fields do not match'
})

extend('between', {
  ...between,
  message: 'Input a value between {min} and {max}'
})

extend('max', {
  ...max,
  message: 'Please do not enter more than {length} characters'
})

extend('min_digits', {
  ...min,
  message: 'Please enter at least {length} digits'
})

extend('max_digits', {
  ...max,
  message: 'Please do not enter more than {length} digits'
})

extend('url', {
  validate (value) {
    const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator
    return pattern.test(value)
  },
  message: 'Please enter a valid url.'
})

extend('http_url', {
  validate (value) {
    return /^https?:\/\/\S+$/i.test(value)
  },
  message: 'Please enter a full URL starting with http:// or https://'
})

extend('phone_number', {
  validate (value) {
    const pattern = new RegExp(/^\d*$/, 'i')
    return pattern.test(value)
  },
  message: 'Please enter only numbers.'
})

extend('image', {
  validate (value, params) {
    return params.includes(value.type)
  },
  message: 'Please select a valid image.'
})

extend('image_size_2mg', {
  validate (value) {
    return value.size < 2000000
  },
  message: 'Image size should be less than 2 MB.'
})

extend('profile_photo_type', {
  validate (value) {
    if (!value) return true
    return isAllowedProfilePhotoType(value)
  },
  message: PROFILE_PHOTO_MESSAGES.type
})

extend('profile_photo_size', {
  validate (value) {
    if (!value) return true
    return isAllowedProfilePhotoSize(value)
  },
  message: PROFILE_PHOTO_MESSAGES.size
})

extend('valid_product_image', {
  validate (value) {
    if (!value) {
      return true
    }
    const allowedTypes = ['image/png', 'image/jpeg']
    return allowedTypes.includes(value.type) && value.size < 5000000
  },
  message: 'Please select a PNG or JPG image under 5 MB.'
})

extend('unique_product_code', {
  params: ['codes'],
  validate (value, { codes }: Record<string, any>) {
    if (!value || !codes || codes.length === 0) {
      return true
    }
    return !codes.includes(String(value).trim().toLowerCase())
  },
  message: 'This product code is already in use.'
})

setInteractionMode('eagerTextCustomRule', () => {
  return {
    on: ['change', 'blur', 'input']
  }
})

extend('isValidColorFormat', {
  message: 'The colour must have a right format. example "#000" or "#000000" or "#00000000"',
  validate: value => {
    const colourFormatRegExp = new RegExp('^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3}|[a-fA-F0-9]{8})$')
    return colourFormatRegExp.test(value + '')
  }
})

extend('min_value', {
  ...min_value,
  message: 'Value must be a number greater than or equal to {min}'
})

extend('max_value', {
  ...max_value,
  message: 'Value must be a number less than or equal to {max}'
})

extend('isLowerThan', {
  validate (value1: any, value2: any) {
    let validationResult = false
    value1 > parseInt(value2[0]) ? validationResult = false : validationResult = true
    return validationResult
  },
  message: 'The value must be lower than highest'
})

extend('isGreaterThan', {
  validate (value1: any, value2: any) {
    let validationResult = false
    value1 < parseInt(value2[0]) ? validationResult = false : validationResult = true
    return validationResult
  },
  message: 'The value must be greater than the lowest'
})

extend('stripe', {
  validate: value => value,
  message: 'The {_field_} field is incomplete or invalid.'
})

extend('unexpired', {
  validate (value: string) {
    const currentYear = parseInt(moment().format('YY'), 10)
    const inputYear = parseInt(value, 10)
    return inputYear >= currentYear
  },
  message: 'Your credit card must be unexpired'
})

extend('credit_card', {
  validate (value: string) {
    // Remove spaces or dashes
    const sanitizedValue = value.replace(/[\s-]/g, '')

    // Check if the value contains only digits
    if (!/^\d+$/.test(sanitizedValue)) {
      return false
    }

    // Luhn algorithm for credit card validation
    let sum = 0
    let shouldDouble = false

    for (let i = sanitizedValue.length - 1; i >= 0; i--) {
      let digit = parseInt(sanitizedValue[i], 10)

      if (shouldDouble) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      shouldDouble = !shouldDouble
    }

    return sum % 10 === 0
  },
  message: 'Please enter a valid credit card number.'
})

extend('gl_code', {
  validate (value: string) {
    // GL Code: alphanumeric, dashes allowed, 3-20 characters
    return /^[A-Za-z0-9-]{3,20}$/.test(value)
  },
  message: 'Enter a valid GL Code (alphanumeric, dashes allowed, 3-20 characters).'
})

extend('csv_has_count', {
  params: ['count'],
  validate (value, { count }: Record<string, any>) {
    if (count) {
      const valueToArray = value.split(',').map(s => s.trim())
      for (const item of valueToArray) {
        if (item.length === 0) {
          return false
        }
      }
      return valueToArray.length === parseInt(count)
    } else {
      return false
    }
  },
  message: 'The {_field_} field must have exactly {count} items.'
})

extend('csv_has_sum', {
  params: ['sum'],
  validate (value, { sum }: Record<string, any>) {
    if (sum) {
      const valueToArray = value.split(',').map(s => s.trim())
      return valueToArray.reduce((acc, val) => acc + parseInt(val), 0) === parseInt(sum)
    } else {
      return false
    }
  },
  message: 'The {_field_} field must total {sum}.'
})

Vue.component('validation-provider', ValidationProvider)
Vue.component('validation-observer', ValidationObserver)
